import React, { useState, useEffect } from 'react';
import { Upload, Palette, Eye, Save, RotateCcw, Image, X, Download, Monitor } from 'lucide-react';
import { api, API_BASE_URL } from './services/apiClient';
import { logger } from './services/logger';

const BrandingModule = ({
  useAuth,
  usePermissions,
  useNotification,
  useTheme,
  BaseButton,
  BaseInput,
  BaseCard
}) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { addNotification } = useNotification();
  const { theme, updateTheme } = useTheme();

  const [loading, setLoading]           = useState(false);
  const [fetching, setFetching]         = useState(true);
  const [previewMode, setPreviewMode]   = useState(false);
  const [logoFile, setLogoFile]         = useState(null);
  const [logoPreview, setLogoPreview]   = useState(null);
  const [companyName, setCompanyName]   = useState('');
  const [colors, setColors]             = useState({
    primary:   '#8BA63A',
    secondary: '#3D4E8C',
    accent:    '#4E61A3'
  });
  const [savedInfo, setSavedInfo]       = useState({ updatedAt: null, updatedBy: '' });
  const [originalSettings, setOriginalSettings] = useState(null);

  const [addinVersion, setAddinVersion]     = useState(null);
  const [addinDownloading, setAddinDownloading] = useState(false);

  // Aplica las CSS variables al DOM para theming dinámico
  const applyCssVariables = (primary, secondary, accent) => {
    const root = document.documentElement;
    root.style.setProperty('--primary', primary);
    root.style.setProperty('--primary-hover', accent);
    root.style.setProperty('--secondary', secondary);
  };

  // Carga el branding actual desde el backend solo al montar el módulo
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFetching(true);
      try {
        const data = await api.get('/api/tenants/branding');
        if (cancelled) return;
        const initial = {
          companyName: data.companyName || '',
          colors: {
            primary:   data.primaryColor   || '#8BA63A',
            secondary: data.secondaryColor || '#3D4E8C',
            accent:    data.accentColor    || '#4E61A3'
          },
          logoUrl: data.logoUrl || null
        };
        setCompanyName(initial.companyName);
        setColors(initial.colors);
        setLogoPreview(initial.logoUrl ? `${API_BASE_URL()}${initial.logoUrl}` : null);
        setSavedInfo({ updatedAt: data.updatedAt, updatedBy: data.updatedBy });
        setOriginalSettings(initial);
      } catch (err) {
        if (cancelled) return;
        addNotification('Error al cargar la configuración de la empresa', 'error');
        logger.error('Error cargando branding', { error: err.message });
      } finally {
        if (!cancelled) setFetching(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Consulta la versión disponible del Add-in
  useEffect(() => {
    fetch(`${API_BASE_URL()}/api/v1/addin/version`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAddinVersion(data); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Verificar permisos
  if (!hasPermission('manage_company') && user?.role !== 'admin') {
    return (
      <BaseCard title="Acceso Denegado">
        <div className="text-center py-8">
          <X className="mx-auto text-red-500 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Sin permisos</h3>
          <p className="text-gray-600">No tienes permisos para acceder a la configuración de la empresa.</p>
        </div>
      </BaseCard>
    );
  }

  // Manejar carga de logo
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      addNotification('Formato de archivo no válido. Solo se permiten PNG, JPG y SVG.', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      addNotification('El archivo es muy grande. Máximo 5 MB permitido.', 'error');
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleColorChange = (colorType, value) => {
    setColors(prev => ({ ...prev, [colorType]: value }));
  };

  // Aplica preview visual sin guardar
  const applyPreview = () => {
    setPreviewMode(true);
    applyCssVariables(colors.primary, colors.secondary, colors.accent);
    updateTheme({ primary: colors.primary, secondary: colors.secondary, accent: colors.accent });
    addNotification('Vista previa aplicada', 'info');
  };

  // Guarda en el backend via multipart/form-data
  const handleSave = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('CompanyName', companyName.trim());
      formData.append('PrimaryColor', colors.primary);
      formData.append('SecondaryColor', colors.secondary);
      formData.append('AccentColor', colors.accent);
      if (logoFile) {
        formData.append('LogoFile', logoFile);
      }

      const result = await api.put('/api/tenants/branding', formData);

      applyCssVariables(result.primaryColor, result.secondaryColor, result.accentColor);
      const logoUrl = result.logoUrl ? `${API_BASE_URL()}${result.logoUrl}` : logoPreview;
      updateTheme({
        logo:      logoUrl,
        primary:   result.primaryColor,
        secondary: result.secondaryColor,
        accent:    result.accentColor,
        companyName: result.companyName
      });

      setSavedInfo({ updatedAt: result.updatedAt, updatedBy: result.updatedBy });
      setOriginalSettings({
        companyName: result.companyName,
        colors: {
          primary:   result.primaryColor,
          secondary: result.secondaryColor,
          accent:    result.accentColor
        },
        logoUrl: result.logoUrl
      });
      setLogoFile(null);
      setPreviewMode(false);
      addNotification('Configuración de empresa guardada correctamente', 'success');

      logger.info('AUDIT LOG', {
        action: 'UPDATE_BRANDING',
        user: user.username,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      addNotification(err.message || 'Error al guardar la configuración', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Descarta cambios locales y restaura desde originalSettings
  const handleReset = () => {
    if (!originalSettings) return;
    setCompanyName(originalSettings.companyName);
    setColors(originalSettings.colors);
    setLogoPreview(originalSettings.logoUrl ? `${API_BASE_URL()}${originalSettings.logoUrl}` : null);
    setLogoFile(null);
    setPreviewMode(false);
    applyCssVariables(originalSettings.colors.primary, originalSettings.colors.secondary, originalSettings.colors.accent);
    updateTheme({
      primary:   originalSettings.colors.primary,
      secondary: originalSettings.colors.secondary,
      accent:    originalSettings.colors.accent
    });
    addNotification('Cambios descartados', 'info');
    const fileInput = document.getElementById('logo-upload');
    if (fileInput) fileInput.value = '';
  };

  const hasChanges = () => {
    if (!originalSettings) return false;
    return (
      companyName.trim() !== originalSettings.companyName ||
      logoFile !== null ||
      colors.primary   !== originalSettings.colors.primary ||
      colors.secondary !== originalSettings.colors.secondary ||
      colors.accent    !== originalSettings.colors.accent
    );
  };

  // Descarga el Add-in usando el JWT del usuario autenticado
  const handleDownloadAddin = async () => {
    if (!addinVersion?.downloadUrl) {
      addNotification('La URL de descarga no está disponible. Contacte al administrador del sistema.', 'error');
      return;
    }
    setAddinDownloading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(addinVersion.downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `Error ${response.status}`);
      }
      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'GEOCheck.Addin.esriAddinX';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      addNotification('Add-in descargado correctamente', 'success');
    } catch (err) {
      addNotification(err.message || 'Error al descargar el Add-in. Verifique que el archivo esté disponible en el servidor.', 'error');
    } finally {
      setAddinDownloading(false);
    }
  };

  const ColorPreview = ({ color, label }) => (
    <div className="flex items-center space-x-3">
      <div className="w-8 h-8 rounded-full border-2 border-gray-300" style={{ backgroundColor: color }} />
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{color.toUpperCase()}</p>
      </div>
    </div>
  );

  if (fetching) {
    return (
      <BaseCard>
        <div className="text-center py-12 text-gray-500">Cargando configuración...</div>
      </BaseCard>
    );
  }

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <BaseCard>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Mi Empresa — {companyName || 'Sin nombre configurado'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Gestiona la identidad visual y las herramientas de tu organización
            </p>
          </div>
          {previewMode && (
            <div className="flex items-center space-x-2 bg-blue-50 px-3 py-2 rounded-lg">
              <Eye size={16} className="text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Modo Vista Previa</span>
            </div>
          )}
        </div>
      </BaseCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel de configuración */}
        <div className="space-y-6">
          {/* Nombre de la empresa */}
          <BaseCard title="Nombre de la Empresa">
            <BaseInput
              label="Nombre"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Ej: 10S SAS"
              maxLength={255}
            />
          </BaseCard>

          {/* Logo */}
          <BaseCard title="Logo Corporativo">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subir Logo</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    id="logo-upload"
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <label htmlFor="logo-upload" className="cursor-pointer">
                    <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                    <p className="text-sm text-gray-600">Haz clic para subir un logo</p>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG, SVG hasta 5 MB</p>
                  </label>
                </div>
              </div>

              {logoPreview && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vista Previa del Logo</label>
                  <div className="bg-gray-50 p-4 rounded-lg flex items-center justify-center">
                    <img src={logoPreview} alt="Logo preview" className="max-h-20 max-w-40 object-contain" />
                  </div>
                </div>
              )}
            </div>
          </BaseCard>

          {/* Colores */}
          <BaseCard title="Colores Corporativos">
            <div className="space-y-6">
              {[
                { key: 'primary',   label: 'Color Principal',   hint: 'Fondos y encabezados',    placeholder: '#8BA63A' },
                { key: 'secondary', label: 'Color Secundario',  hint: 'Botones y componentes',   placeholder: '#3D4E8C' },
                { key: 'accent',    label: 'Color de Acento',   hint: 'Hover, bordes e íconos',  placeholder: '#4E61A3' }
              ].map(({ key, label, hint, placeholder }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={colors[key]}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <BaseInput
                      value={colors[key]}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      placeholder={placeholder}
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{hint}</p>
                </div>
              ))}
            </div>
          </BaseCard>
        </div>

        {/* Panel derecho */}
        <div className="space-y-6">
          <BaseCard title="Vista Previa de la Interfaz">
            <div className="space-y-4">
              <div className="p-4 rounded-lg text-white" style={{ backgroundColor: colors.primary }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {logoPreview ? (
                      <div className="w-8 h-8 bg-white rounded p-1">
                        <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-8 h-8 bg-white bg-opacity-20 rounded flex items-center justify-center">
                        <Image size={16} />
                      </div>
                    )}
                    <span className="font-bold">{companyName || 'GEOCheck'}</span>
                  </div>
                  <div className="text-sm opacity-90">Dashboard</div>
                </div>
              </div>

              <div className="space-y-3">
                <button className="w-full py-2 px-4 rounded-lg text-white font-medium" style={{ backgroundColor: colors.secondary }}>
                  Botón Secundario
                </button>
                <button className="w-full py-2 px-4 rounded-lg text-white font-medium" style={{ backgroundColor: colors.accent }}>
                  Botón de Acento
                </button>
              </div>

              <div className="p-3 border-l-4 bg-gray-50 rounded" style={{ borderLeftColor: colors.accent }}>
                <p className="text-sm font-medium text-gray-900">Elemento destacado</p>
                <p className="text-xs text-gray-600">Con borde de color de acento</p>
              </div>
            </div>
          </BaseCard>

          <BaseCard title="Configuración Actual">
            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Paleta de Colores</p>
              <div className="space-y-3">
                <ColorPreview color={colors.primary}   label="Color Principal" />
                <ColorPreview color={colors.secondary} label="Color Secundario" />
                <ColorPreview color={colors.accent}    label="Color de Acento" />
              </div>

              {savedInfo.updatedAt && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Última actualización: {new Date(savedInfo.updatedAt).toLocaleString('es-CO')}
                  </p>
                  <p className="text-xs text-gray-500">Por: {savedInfo.updatedBy}</p>
                </div>
              )}
            </div>
          </BaseCard>
        </div>
      </div>

      {/* Add-in para ArcGIS Pro */}
      <BaseCard title="Add-in GEOCheck para ArcGIS Pro">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-blue-50 rounded-lg flex-shrink-0">
              <Monitor size={28} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                GEOCheck.Addin.esriAddinX
              </p>
              <p className="text-sm text-gray-600 mt-0.5">
                Valida geodatabases directamente desde ArcGIS Pro sin salir del entorno de trabajo.
              </p>
              <div className="flex items-center gap-4 mt-2">
                {addinVersion?.latestVersion ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    v{addinVersion.latestVersion}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                    Versión no disponible
                  </span>
                )}
                <span className="text-xs text-gray-500">Requiere ArcGIS Pro 3.x · Windows 64-bit</span>
              </div>
            </div>
          </div>

          <BaseButton
            onClick={handleDownloadAddin}
            icon={Download}
            variant="primary"
            disabled={addinDownloading || !addinVersion?.downloadUrl}
          >
            {addinDownloading ? 'Descargando...' : 'Descargar'}
          </BaseButton>
        </div>

        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-600 font-medium mb-1">Instrucciones de instalación</p>
          <ol className="text-xs text-gray-500 space-y-1 list-decimal list-inside">
            <li>Descarga el archivo <code className="bg-gray-100 px-1 rounded">.esriAddinX</code> con el botón de arriba.</li>
            <li>Cierra ArcGIS Pro si está abierto.</li>
            <li>Haz doble clic en el archivo descargado para instalarlo.</li>
            <li>Abre ArcGIS Pro — el add-in aparece en la pestaña <strong>GEOCheck</strong> de la cinta.</li>
          </ol>
        </div>
      </BaseCard>

      {/* Botones de acción */}
      <BaseCard>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div className="flex flex-wrap gap-3">
            <BaseButton onClick={applyPreview} icon={Eye} variant="outline" disabled={!hasChanges()}>
              Vista Previa
            </BaseButton>
            <BaseButton onClick={handleReset} icon={RotateCcw} variant="outline" disabled={!hasChanges()}>
              Descartar Cambios
            </BaseButton>
          </div>

          <BaseButton onClick={handleSave} icon={Save} disabled={loading || !hasChanges()} variant="primary">
            {loading ? 'Guardando...' : 'Guardar Configuración'}
          </BaseButton>
        </div>

        {hasChanges() && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-yellow-400 rounded-full" />
              <p className="text-sm text-yellow-800">Tienes cambios sin guardar. No olvides guardar tu configuración.</p>
            </div>
          </div>
        )}
      </BaseCard>
    </div>
  );
};

export default BrandingModule;
